import type { ISODate } from './common';

export type ThemePreference = 'light' | 'dark' | 'system';

export interface NotificationPreferences {
  push: boolean;
  email: boolean;
  marketing: boolean;
  productUpdates: boolean;
  sound: boolean;
  soundName: string;
}

export interface PrivacyPreferences {
  locationAccess: boolean;
  analytics: boolean;
  personalization: boolean;
  crashReports: boolean;
}

export interface SecurityPreferences {
  twoFactorEnabled: boolean;
  appLockEnabled: boolean;
  appLockMethod: 'none' | 'pin' | 'biometric';
}

export interface UserSettings {
  theme: ThemePreference;
  language: string;
  notifications: NotificationPreferences;
  privacy: PrivacyPreferences;
  security: SecurityPreferences;
  updatedAt: ISODate;
}

/** Update payload — top-level fields and/or partial nested sections. */
export type SettingsPatch =
  Partial<Omit<UserSettings, 'notifications' | 'privacy' | 'security' | 'updatedAt'>> & {
    notifications?: Partial<NotificationPreferences>;
    privacy?: Partial<PrivacyPreferences>;
    security?: Partial<SecurityPreferences>;
  };

export interface LanguageOption {
  code: string;
  label: string;
  native: string;
}

export const LANGUAGES: LanguageOption[] = [
  { code: 'en', label: 'English', native: 'English' },
  { code: 'es', label: 'Spanish', native: 'Español' },
  { code: 'fr', label: 'French', native: 'Français' },
  { code: 'de', label: 'German', native: 'Deutsch' },
  { code: 'pt', label: 'Portuguese', native: 'Português' },
  { code: 'ar', label: 'Arabic', native: 'العربية' },
  { code: 'zh', label: 'Chinese', native: '中文' },
  { code: 'ja', label: 'Japanese', native: '日本語' },
];

export const NOTIFICATION_SOUNDS = ['Default', 'Chime', 'Pulse', 'Ripple', 'None'] as const;

export const DEFAULT_SETTINGS: UserSettings = {
  theme: 'system',
  language: 'en',
  notifications: {
    push: true,
    email: true,
    marketing: false,
    productUpdates: true,
    sound: true,
    soundName: 'Default',
  },
  privacy: {
    locationAccess: false,
    analytics: true,
    personalization: true,
    crashReports: true,
  },
  security: {
    twoFactorEnabled: false,
    appLockEnabled: false,
    appLockMethod: 'none',
  },
  updatedAt: new Date(0).toISOString(),
};
